/**
 * RFQDetailDashboard Component
 * 
 * Detailed view for a specific RFQ request showing all products and supplier information.
 * This page is accessed via /dashboard/rfqs/open/{request_id} route.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import AdminLayout from '../../layout/AdminLayout';
import UserLayout from '../../../user-v2/layout/UserLayout';
// Removed SecondarySidebar - now using dynamic sub-items in main sidebar
import { AdminUser, AdminRole } from '../../../../types/admin-v2';
import { API_ENDPOINTS } from '../../../../utils/config';
import { useAuth } from '../../../../context/AuthContext';
import { getDynamicSubItems } from '../../../../utils/getDynamicSubItems';
import { getRFQNavigationInfo, getRFQPageRoute, getRFQPageName } from '../../../../utils/rfqNavigation';
import './RFQDetailDashboard.scss';

interface RFQDetailDashboardProps {
  user: AdminUser | { id: string; name: string; email: string; role?: 'user' | 'admin' | 'useradmin'; permissions?: any };
  currentRoute: string;
  onNavigate: (route: string) => void;
  onLogout: () => void;
  isReadOnly?: boolean;
}

const RFQDetailDashboard: React.FC<RFQDetailDashboardProps> = ({
  user,
  currentRoute,
  onNavigate,
  onLogout,
  isReadOnly = false
}) => {
  const { token } = useAuth();
  const params = useParams<{ requestId: string }>();
  const requestId = params.requestId;
  const [searchParams] = useSearchParams();
  const fromInbox = searchParams.get('from') === 'inbox';
  const fromFinalized = searchParams.get('from') === 'finalized' || document.referrer.includes('/closed-rfq') || document.referrer.includes('/finalized');
  const inboxId = searchParams.get('inboxId') || null; // Get inbox ID from URL if available

  // Determine if this is basic plan user route (basic plan users use /dashboard/user/rfq/)
  const isBasicPlanRoute = currentRoute.startsWith('/dashboard/user/rfq/');
  const isAdminRoute = currentRoute.startsWith('/dashboard/admin/request/') || currentRoute.startsWith('/dashboard/rfqs/open/');
  const baseRoute = isBasicPlanRoute ? '/dashboard/user/rfq' : isAdminRoute ? '/dashboard/rfqs/open' : '/dashboard/rfqs/open';

  // Check Process RFQ setting - if Auto AI, don't show secondary sidebar (old behavior)
  // Backend stores process_rfq as boolean: true = "auto ai", false = "manual"
  const processRfq = (user as any)?.process_rfq ?? (user as any)?.permissions?.process_rfq;
  const isAutoAI = processRfq === true || processRfq === 'auto ai' || processRfq === 'Auto AI' || processRfq === 'auto_ai';

  // Debug: Log process_rfq value
  useEffect(() => {
    console.log('🔍 RFQDetailDashboard - process_rfq check:', {
      processRfq,
      isAutoAI,
      userProcessRfq: (user as any)?.process_rfq,
      userPermissionsProcessRfq: (user as any)?.permissions?.process_rfq,
      showSecondarySidebar: isAutoAI ? false : (isBasicPlanRoute || (!isReadOnly))
    });
  }, [processRfq, isAutoAI, isBasicPlanRoute, isReadOnly]);

  // For Auto AI users, NEVER show secondary sidebar (old behavior) - regardless of route
  // For Manual Process RFQ users on /dashboard/user/rfq/ route, show secondary sidebar
  // For admins, show secondary sidebar if not read-only
  // For read-only users (Pro plan), don't show secondary sidebar
  const showSecondarySidebar = isAutoAI ? false : (isBasicPlanRoute || (!isReadOnly));

  // Debug: Log requestId and route info
  useEffect(() => {
    console.log('RFQDetailDashboard params:', { requestId, currentRoute, isAdminRoute, baseRoute });
  }, [requestId, currentRoute, isAdminRoute, baseRoute]);

  // Determine inbox route based on user role and route context
  const inboxRoute = (isBasicPlanRoute || isReadOnly)
    ? '/dashboard/user/inbox/open'
    : '/dashboard/rfqs/open';

  const [rfqData, setRfqData] = useState<any>(null);
  const [products, setProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotesSelections, setQuotesSelections] = useState<any[]>([]);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);

  // Format phase name helper
  const formatPhaseName = (phase: string) => {
    const phaseMap: Record<string, string> = {
      'document_preparation': 'Document Preparation',
      'define_supplier_list': 'Define Supplier List',
      'send_out_rfq': 'Send Out RFQ',
      'receive_responses': 'Receive Responses',
      'select_supplier': 'Select the Supplier',
      'advise_unsuccessful_suppliers': 'Advise Unsuccessful Suppliers',
      'completed': 'Completed',
      'rejected': 'Rejected',
      'cancelled': 'Cancelled',
      'deleted': 'Deleted'
    };
    return phaseMap[phase] || phase;
  };

  // Extract requestId from URL if not in params (fallback for different route patterns)
  const actualRequestId = useMemo(() => {
    if (requestId) return requestId;
    if (currentRoute) {
      // Try to extract from /dashboard/user/rfq/:id, /dashboard/admin/request/:id, or /dashboard/rfqs/open/:id
      const match = currentRoute.match(/\/(?:dashboard\/)?(?:user\/rfq|admin\/request|rfqs\/open)\/([^\/\?]+)/);
      if (match) {
        console.log('RFQDetailDashboard: Extracted requestId from URL:', match[1]);
        return match[1];
      }
    }
    return null;
  }, [requestId, currentRoute]);

  // Load RFQ details
  useEffect(() => {
    if (!actualRequestId || !token) {
      console.log('RFQDetailDashboard: Missing requestId or token', {
        requestId: actualRequestId,
        hasToken: !!token,
        currentRoute,
        params
      });
      return;
    }

    console.log('RFQDetailDashboard: Starting fetch for requestId:', actualRequestId);

    // Reset state when requestId changes
    setRfqData(null);
    setProducts([]);
    setQuotesSelections([]);
    setError(null);

    const fetchRFQDetails = async () => {
      setIsLoading(true);
      setIsLoadingProducts(true);

      try {
        const rfqUrl = API_ENDPOINTS.REQUEST_V2_DETAIL(actualRequestId);
        const productsUrl = API_ENDPOINTS.REQUEST_V2_PRODUCTS(actualRequestId);
        console.log('RFQDetailDashboard: Fetching from URLs:', { rfqUrl, productsUrl });

        // Fetch RFQ details and products in parallel
        // Use REQUEST_V2_DETAIL endpoint (same as other dashboards)
        const [rfqResponse, productsResponse] = await Promise.all([
          fetch(rfqUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }),
          fetch(productsUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        ]);

        console.log('RFQDetailDashboard: Responses received', {
          rfqStatus: rfqResponse.status,
          productsStatus: productsResponse.status
        });

        if (!rfqResponse.ok) {
          const errorText = await rfqResponse.text();
          console.error('RFQ fetch error:', rfqResponse.status, errorText);
          throw new Error(`Failed to fetch RFQ details: ${rfqResponse.status} ${rfqResponse.statusText}`);
        }

        const data = await rfqResponse.json();
        console.log('RFQ Details API response:', data);

        if (!data.success || !data.data) {
          console.error('Invalid RFQ data:', data);
          throw new Error(data.message || 'Invalid RFQ data received');
        }

        setRfqData(data.data);

        // Access Control for Basic Users: Redirect if phase is completed or select_supplier
        if (isBasicPlanRoute) {
          if (data.data.phase === 'completed') {
            console.log('🔒 Redirecting completed request to finalized inbox');
            onNavigate('/dashboard/user/inbox/finalized');
            return;
          }
          if (data.data.phase === 'select_supplier') {
            console.log('🔒 Redirecting select_supplier request to open inbox');
            onNavigate('/dashboard/user/inbox/open');
            return;
          }
        }

        // If phase is select_supplier, fetch quotes_selections for summary view
        if (data.data.phase === 'select_supplier' && data.data.quotes_selections) {
          setQuotesSelections(data.data.quotes_selections || []);
        }

        // Handle products response
        if (productsResponse.ok) {
          const productsData = await productsResponse.json();
          console.log('✅ RFQ Products API response:', productsData);

          if (productsData.success && productsData.data) {
            console.log('📊 Products count:', productsData.data.length);
            setProducts(productsData.data);
          } else {
            console.log('⚠️ No products data in response');
            setProducts([]);
          }
        } else {
          console.log('❌ Products fetch failed:', productsResponse.statusText);
          setProducts([]);
        }
      } catch (error: any) {
        console.error('Error fetching RFQ details:', error);
        setError(error.message || 'Failed to load RFQ details');
      } finally {
        setIsLoading(false);
        setIsLoadingProducts(false);
      }
    };

    fetchRFQDetails();
  }, [actualRequestId, token]);

  // Dynamic sub-items function for sidebar - use imported getDynamicSubItems
  const getDynamicSubItemsWrapper = useMemo(() => {
    // Only return function when we're on an RFQ detail page and have a requestId
    // Support admin routes (/rfqs/open/, /admin/request/) and user routes (/user/rfq/)
    if (!actualRequestId || (!currentRoute.includes('/rfqs/open/') && !currentRoute.includes('/admin/request/') && !currentRoute.includes('/user/rfq/'))) {
      return undefined;
    }

    // Return the imported getDynamicSubItems function directly
    return getDynamicSubItems;
  }, [actualRequestId, currentRoute]);

  // Navigation actions for user layouts
  const navInfo = useMemo(() => getRFQNavigationInfo(currentRoute), [currentRoute]);
  const navigationActions = useMemo(() => {
    // Show navigation buttons for all users (including admins)
    // if (isBasicPlanRoute || isReadOnly) {
    {
      const hasPreviousPage = navInfo?.previousPage;
      const hasNextPage = navInfo?.nextPage;
      const showBackToInbox = !hasPreviousPage && fromInbox;
      const showBackToOpenRFQs = !hasPreviousPage && !fromInbox && navInfo?.currentPage === 'detail';

      if (!hasPreviousPage && !hasNextPage && !showBackToInbox && !showBackToOpenRFQs) {
        return undefined;
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', width: '100%', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {hasPreviousPage && navInfo?.previousPage ? (
              <button
                onClick={() => {
                  if (navInfo && navInfo.previousPage) {
                    onNavigate(getRFQPageRoute(navInfo.baseRoute, navInfo.requestId, navInfo.previousPage));
                  }
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                ← Back to {getRFQPageName(navInfo.previousPage)}
              </button>
            ) : showBackToInbox ? (
              <button
                onClick={() => {
                  if (inboxId) {
                    onNavigate(`${inboxRoute}?inboxId=${inboxId}`);
                  } else {
                    onNavigate(inboxRoute);
                  }
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                ← Back to Open RFQs
              </button>
            ) : showBackToOpenRFQs ? (
              <button
                onClick={() => {
                  onNavigate(inboxRoute);
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                ← Back to Open RFQs
              </button>
            ) : null}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {hasNextPage && navInfo?.nextPage && (
              <button
                onClick={() => {
                  if (navInfo.nextPage) {
                    onNavigate(getRFQPageRoute(navInfo.baseRoute, navInfo.requestId, navInfo.nextPage));
                  }
                }}
                style={{
                  padding: '8px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  backgroundColor: '#ffffff',
                  color: '#374151',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                Next to {navInfo.nextPage ? getRFQPageName(navInfo.nextPage) : ''} →
              </button>
            )}
          </div>
        </div>
      );
    }
    return undefined;
  }, [navInfo, isBasicPlanRoute, isReadOnly, fromInbox, inboxId, inboxRoute, onNavigate, currentRoute]);

  // Helper function to convert user to AdminUser for AdminLayout
  const getUserForLayout = (): AdminUser => {
    if ('role' in user && (user.role === 'admin' || user.role === 'useradmin')) {
      return user as AdminUser;
    }
    // For read-only users, convert to AdminUser format
    return {
      id: user.id,
      name: user.name,
      email: user.email,
      role: 'useradmin' as AdminRole,
      permissions: user.permissions || {}
    };
  };

  // Loading state - show loading only if both RFQ and products are loading
  if (isLoading || isLoadingProducts || !rfqData) {
    // Basic plan users: UserLayout with secondary sidebar
    if (isBasicPlanRoute) {
      return (
        <UserLayout
          user={user}
          currentRoute={currentRoute}
          onNavigate={onNavigate}
          onLogout={onLogout}
          pageTitle="Loading RFQ Details..."
        >
          <div className="rfq-detail-loading">
            <div className="spinner"></div>
            <h2>Loading RFQ Details...</h2>
            <p>Please wait while we fetch the request information</p>
          </div>
        </UserLayout>
      );
    }
    // Read-only users (Pro plan): UserLayout without secondary sidebar
    if (isReadOnly) {
      return (
        <UserLayout
          user={user}
          currentRoute={currentRoute}
          onNavigate={onNavigate}
          onLogout={onLogout}
          pageTitle="Loading RFQ Details..."
        >
          <div className="rfq-detail-loading">
            <div className="spinner"></div>
            <h2>Loading RFQ Details...</h2>
            <p>Please wait while we fetch the request information</p>
          </div>
        </UserLayout>
      );
    }
    // AdminLayout for admins
    return (
      <AdminLayout
        user={getUserForLayout()}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        pageTitle="Loading RFQ Details..."
        getDynamicSubItems={getDynamicSubItemsWrapper}
      >
        <div className="rfq-detail-loading">
          <div className="spinner"></div>
          <h2>Loading RFQ Details...</h2>
          <p>Please wait while we fetch the request information</p>
        </div>
      </AdminLayout>
    );
  }

  // Error state
  if (error) {
    // Basic plan users: UserLayout
    if (isBasicPlanRoute) {
      return (
        <UserLayout
          user={user}
          currentRoute={currentRoute}
          onNavigate={onNavigate}
          onLogout={onLogout}
          pageTitle="Error"
        >
          <div className="error-state" style={{ padding: '2rem' }}>
            <h2>Error Loading RFQ</h2>
            <p>{error}</p>
            <button
              onClick={() => onNavigate('/dashboard/user/inbox/open')}
              style={{
                padding: '8px 16px',
                backgroundColor: '#fff',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer',
                marginTop: '1rem'
              }}
            >
              ← Back to Inbox
            </button>
          </div>
        </UserLayout>
      );
    }
    // Read-only users (Pro plan): UserLayout
    if (isReadOnly) {
      return (
        <UserLayout
          user={user}
          currentRoute={currentRoute}
          onNavigate={onNavigate}
          onLogout={onLogout}
          pageTitle="Error"
        >
          <div className="error-state" style={{ padding: '2rem' }}>
            <h2>Error Loading RFQ</h2>
            <p>{error}</p>
          </div>
        </UserLayout>
      );
    }
    // Admins: AdminLayout
    return (
      <AdminLayout
        user={getUserForLayout()}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        pageTitle="Error"
        getDynamicSubItems={getDynamicSubItemsWrapper}
      >
        <div className="error-state" style={{ padding: '2rem' }}>
          <h2>Error Loading RFQ</h2>
          <p>{error}</p>
          <button
            onClick={() => onNavigate('/dashboard/rfqs/open')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#fff',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '1rem'
            }}
          >
            ← Back to Open RFQs
          </button>
        </div>
      </AdminLayout>
    );
  }

  // No data state
  if (!rfqData) {
    if (isReadOnly) {
      return (
        <UserLayout
          user={user}
          currentRoute={currentRoute}
          onNavigate={onNavigate}
          onLogout={onLogout}
          pageTitle="RFQ Not Found"
        >
          <div className="error-state" style={{ padding: '2rem' }}>
            <h2>RFQ Not Found</h2>
            <p>The requested RFQ could not be found.</p>
          </div>
        </UserLayout>
      );
    }
    return (
      <AdminLayout
        user={getUserForLayout()}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        pageTitle="RFQ Not Found"
        getDynamicSubItems={getDynamicSubItemsWrapper}
      >
        <div className="error-state" style={{ padding: '2rem' }}>
          <h2>RFQ Not Found</h2>
          <p>The requested RFQ could not be found.</p>
          <button
            onClick={() => onNavigate('/dashboard/rfqs/open')}
            style={{
              padding: '8px 16px',
              backgroundColor: '#fff',
              color: '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '1rem'
            }}
          >
            ← Back to Open RFQs
          </button>
        </div>
      </AdminLayout>
    );
  }

  // Format quotes selections for summary view (similar to Quotes Review summary)
  const formatQuotesSummary = (selections: any[]) => {
    return selections.map(sel => ({
      supplier_id: sel.supplier_id,
      supplier_readable_id: sel.supplier_readable_id || '',
      supplier_name: sel.supplier_name || 'Unknown Supplier',
      total_price_display: sel.total_price
        ? `${sel.total_price.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${sel.currency || ''}`.trim()
        : '—',
      lead_time: sel.lead_time || '—',
      payment_terms: sel.payment_terms || '—',
      delivery_cost: sel.delivery_cost || '—',
      quote_valid_until: sel.quote_valid_until || '—',
      overall_score_display: sel.overall_score !== null && sel.overall_score !== undefined
        ? `${sel.overall_score.toFixed(2)}`
        : '—',
      quote_link: sel.quote_link || null
    }));
  };

  const summaryRows = formatQuotesSummary(quotesSelections);

  // Handle product selection toggle
  const handleToggleProduct = (productId: string) => {
    setSelectedProductIds(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  // Handle RFQ split
  const handleSplitRFQ = async (productIds?: string[]) => {
    const productsToSplit = productIds || selectedProductIds;

    if (productsToSplit.length === 0) {
      alert('Please select at least one product to split');
      return;
    }

    if (productsToSplit.length === products.length) {
      alert('You cannot split all products. At least one product must remain in the original RFQ.');
      return;
    }

    if (!window.confirm(`Split ${productsToSplit.length} product(s) into a new RFQ?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_ENDPOINTS.REQUESTS_V2}/${actualRequestId}/split`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          product_ids: productsToSplit
        })
      });

      if (!response.ok) {
        throw new Error('Failed to split RFQ');
      }

      const data = await response.json();
      if (data.success) {
        alert(`RFQ split successfully! New RFQ ID: ${data.data.new_request_id}`);
        // Clear selection and reload
        setSelectedProductIds([]);
        window.location.reload();
      } else {
        throw new Error(data.message || 'Failed to split RFQ');
      }
    } catch (error: any) {
      console.error('Error splitting RFQ:', error);
      alert('Failed to split RFQ: ' + error.message);
    }
  };

  const summaryMetrics = [
    {
      key: 'total_price_display',
      label: 'Total Price',
      render: (row: any) => row.total_price_display || '—'
    },
    {
      key: 'lead_time',
      label: 'Lead Time',
      render: (row: any) => row.lead_time || '—'
    },
    {
      key: 'payment_terms',
      label: 'Payment Terms',
      render: (row: any) => row.payment_terms || '—'
    },
    {
      key: 'delivery_cost',
      label: 'Delivery Cost',
      render: (row: any) => row.delivery_cost || '—'
    },
    {
      key: 'quote_valid_until',
      label: 'Quote Valid Until',
      render: (row: any) => row.quote_valid_until || '—'
    },
    {
      key: 'overall_score_display',
      label: 'Overall Evaluation',
      render: (row: any) => row.overall_score_display || '—'
    },
    {
      key: 'quote_link',
      label: 'Quote',
      render: (row: any) =>
        row.quote_link ? (
          <a
            href={row.quote_link}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#3b82f6', textDecoration: 'underline' }}
          >
            Download Quote
          </a>
        ) : (
          '—'
        )
    }
  ];

  // Show summary view if phase is select_supplier and user is read-only (regular user)
  const showSummaryView = isReadOnly && rfqData?.phase === 'select_supplier' && quotesSelections.length > 0;

  // Main content component (shared between AdminLayout and UserLayout)
  const mainContent = (
    <>
      <div className="rfq-detail-container">

        {/* Summary View for select_supplier phase (read-only users) */}
        {showSummaryView ? (
          <div className="quotes-review-summary" style={{ padding: '1.5rem', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <h2 style={{ margin: '0 0 1.5rem 0', fontSize: '20px', fontWeight: '600' }}>Selected Supplier Quotes</h2>
            {summaryRows.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
                No suppliers selected.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', backgroundColor: '#f9fafb' }}>Metric</th>
                      {summaryRows.map((row) => (
                        <th key={row.supplier_id} style={{ padding: '12px', textAlign: 'left', fontWeight: '600', backgroundColor: '#f9fafb', minWidth: '150px' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{ fontWeight: '600' }}>{row.supplier_name}</span>
                            {row.supplier_readable_id && (
                              <span style={{ fontSize: '12px', color: '#6b7280' }}>{row.supplier_readable_id}</span>
                            )}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {summaryMetrics.map((metric) => (
                      <tr key={metric.key} style={{ borderBottom: '1px solid #e5e7eb' }}>
                        <td style={{ padding: '12px', fontWeight: '500', backgroundColor: '#f9fafb' }}>{metric.label}</td>
                        {summaryRows.map((row) => (
                          <td key={`${row.supplier_id}-${metric.key}`} style={{ padding: '12px' }}>
                            {metric.render(row)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="rfq-detail-content" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {/* Request Information */}
            <div className="rfq-info-section" style={{
              padding: '1.5rem',
              backgroundColor: '#fff',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <h2 style={{ margin: '0 0 1rem 0', fontSize: '18px', fontWeight: '600' }}>Request Information</h2>
              <div className="info-grid" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '1rem'
              }}>
                <div className="info-item">
                  <label style={{ fontWeight: '500', color: '#6b7280' }}>Company:</label>
                  <span style={{ display: 'block', marginTop: '0.25rem' }}>{rfqData.company_name || rfqData.user_name || 'Unknown'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: '500', color: '#6b7280' }}>Created:</label>
                  <span style={{ display: 'block', marginTop: '0.25rem' }}>{rfqData.created_at ? new Date(rfqData.created_at).toLocaleDateString() : 'Unknown'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: '500', color: '#6b7280' }}>Delivery Address:</label>
                  <span style={{ display: 'block', marginTop: '0.25rem' }}>{rfqData.delivery_address || 'Not specified'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: '500', color: '#6b7280' }}>Invoice Address:</label>
                  <span style={{ display: 'block', marginTop: '0.25rem' }}>{rfqData.invoice_address || 'Not specified'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: '500', color: '#6b7280' }}>Contact Person:</label>
                  <span style={{ display: 'block', marginTop: '0.25rem' }}>{rfqData.contact_person || 'Not specified'}</span>
                </div>
                <div className="info-item">
                  <label style={{ fontWeight: '500', color: '#6b7280' }}>Contact Email:</label>
                  <span style={{ display: 'block', marginTop: '0.25rem' }}>{rfqData.contact_email || 'Not specified'}</span>
                </div>
              </div>
            </div>

            {/* Products Table */}
            {products.length > 0 && (
              <div className="rfq-products-section" style={{
                padding: '1.5rem',
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
              }}>
                <h2 style={{ margin: '0 0 1rem 0', fontSize: '18px', fontWeight: '600' }}>Products</h2>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                        {products.length > 1 && !isReadOnly && (
                          <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '50px' }}>
                            <input
                              type="checkbox"
                              checked={selectedProductIds.length === products.length && products.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedProductIds(products.map(p => p._id));
                                } else {
                                  setSelectedProductIds([]);
                                }
                              }}
                              style={{
                                width: '18px',
                                height: '18px',
                                cursor: 'pointer'
                              }}
                            />
                          </th>
                        )}
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Line</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Product Name</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Brand</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Quantity</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Unit</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Budget</th>
                        <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', color: '#374151' }}>Category</th>
                        {products.length > 1 && !isReadOnly && (
                          <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600', color: '#374151', width: '120px' }}>
                            <button
                              onClick={() => handleSplitRFQ()}
                              disabled={selectedProductIds.length === 0}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: selectedProductIds.length > 0 ? '#3b82f6' : '#9ca3af',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: selectedProductIds.length > 0 ? 'pointer' : 'not-allowed',
                                fontSize: '13px',
                                fontWeight: '500',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                if (selectedProductIds.length > 0) {
                                  e.currentTarget.style.backgroundColor = '#2563eb';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (selectedProductIds.length > 0) {
                                  e.currentTarget.style.backgroundColor = '#3b82f6';
                                }
                              }}
                            >
                              Split
                            </button>
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product, index) => (
                        <tr
                          key={product._id || index}
                          style={{
                            borderBottom: '1px solid #e5e7eb',
                            transition: 'background-color 0.2s',
                            backgroundColor: selectedProductIds.includes(product._id) ? '#eff6ff' : 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            if (!selectedProductIds.includes(product._id)) {
                              e.currentTarget.style.backgroundColor = '#f9fafb';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!selectedProductIds.includes(product._id)) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            } else {
                              e.currentTarget.style.backgroundColor = '#eff6ff';
                            }
                          }}
                        >
                          {products.length > 1 && !isReadOnly && (
                            <td style={{ padding: '12px', textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={selectedProductIds.includes(product._id)}
                                onChange={() => handleToggleProduct(product._id)}
                                style={{
                                  width: '18px',
                                  height: '18px',
                                  cursor: 'pointer'
                                }}
                              />
                            </td>
                          )}
                          <td style={{ padding: '12px' }}>{product.line_number || index + 1}</td>
                          <td style={{ padding: '12px', fontWeight: '500' }}>{product.product_name || '-'}</td>
                          <td style={{ padding: '12px' }}>{product.product_brand || '-'}</td>
                          <td style={{ padding: '12px' }}>{product.quantity || '-'}</td>
                          <td style={{ padding: '12px' }}>{product.unit || product.base_unit || '-'}</td>
                          <td style={{ padding: '12px' }}>
                            {product.budget ? `${product.budget} ${product.currency || 'TRY'}` : '-'}
                          </td>
                          <td style={{ padding: '12px' }}>{product.category || '-'}</td>
                          {products.length > 1 && !isReadOnly && (
                            <td style={{ padding: '12px', textAlign: 'center' }}></td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );

  // Basic plan users: UserLayout with secondary sidebar (their own sidebar, not admin)
  if (isBasicPlanRoute) {
    return (
      <UserLayout
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        pageTitle={`RFQ Details - ${rfqData.company_request_id || actualRequestId}`}
        pageDescription="View and manage request details, products, and supplier information"
        getDynamicSubItems={getDynamicSubItemsWrapper}
        navigationActions={navigationActions}
      >
        {mainContent}
      </UserLayout>
    );
  }

  // Read-only users (Pro plan): UserLayout without secondary sidebar
  if (isReadOnly) {
    return (
      <UserLayout
        user={user}
        currentRoute={currentRoute}
        onNavigate={onNavigate}
        onLogout={onLogout}
        pageTitle={`RFQ Details - ${rfqData.company_request_id || actualRequestId}`}
        pageDescription="View and manage request details, products, and supplier information"
        getDynamicSubItems={getDynamicSubItemsWrapper}
        navigationActions={navigationActions}
      >
        {mainContent}
      </UserLayout>
    );
  }

  // Admins: AdminLayout with dynamic sub-items
  return (
    <AdminLayout
      user={getUserForLayout()}
      currentRoute={currentRoute}
      onNavigate={onNavigate}
      onLogout={onLogout}
      pageTitle={`RFQ Details - ${rfqData.company_request_id || actualRequestId}`}
      pageDescription={`Detailed view for request ${rfqData.company_request_id || actualRequestId}`}
      getDynamicSubItems={getDynamicSubItemsWrapper}
      navigationActions={navigationActions}
    >
      {mainContent}
    </AdminLayout>
  );
};

export default RFQDetailDashboard;
